import { FunctionComponent, useRef } from 'react';
import 'chart.js/auto';
import { Pie } from 'react-chartjs-2';
import type { Chart, ActiveElement, Plugin } from 'chart.js';

export type PieChartSlice = {
  label: string;
  value: number;
  color: string;
};

export type PieChartProps = {
  slices: PieChartSlice[];
  size?: number;
  onSliceClick?: (slice: PieChartSlice, index: number) => void;
  onSliceHover?: (index: number | null) => void;
};

type FC = FunctionComponent<PieChartProps>;

type ArcGeometry = {
  x: number;
  y: number;
  startAngle: number;
  endAngle: number;
  outerRadius: number;
};

// Percentage labels are painted inside each slice; light slices get dark ink
// and dark slices light ink so the label always clears its background.
function getLabelColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55 ? '#2D2D2D' : 'rgba(251, 251, 251, 0.9)';
}

const sliceLabelsPlugin: Plugin<'pie'> = {
  id: 'pieSliceLabels',
  afterDatasetsDraw(chart: Chart<'pie'>) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const values = chart.data.datasets[0]?.data ?? [];
    const total = values.reduce((sum: number, value) => sum + Number(value ?? 0), 0);

    if (!total) {
      return;
    }

    // While a slice is hovered it pops out (hoverOffset), so only its own label
    // is drawn — the geometry of the other slices' labels would no longer line up.
    const activeElements = chart.getActiveElements();
    const hoveredIndex = activeElements.length > 0 ? activeElements[0].index : null;

    meta.data.forEach((arc, index) => {
      if (hoveredIndex !== null && hoveredIndex !== index) {
        return;
      }

      // Floor at "<1%" so rounding never shows a 0% label on a visible slice
      const rawPct = (Number(values[index] ?? 0) / total) * 100;
      const pctLabel = rawPct < 1 ? '<1%' : `${Math.round(rawPct)}%`;

      const { x, y, startAngle, endAngle, outerRadius } = arc.getProps(
        ['x', 'y', 'startAngle', 'endAngle', 'outerRadius'],
        true,
      ) as ArcGeometry;
      // Unit vector along the slice's angular center — all label geometry
      // (inside placement, leader line, side of the pie) hangs off this ray
      const midAngle = (startAngle + endAngle) / 2;
      const cos = Math.cos(midAngle);
      const sin = Math.sin(midAngle);

      ctx.save();
      ctx.font = '500 11px Inter, sans-serif';
      ctx.textBaseline = 'middle';

      // Below ~6% the arc is too narrow to fit 11px text inside it
      if (rawPct >= 6) {
        // Wide slice: percentage sits inside, ink chosen against the slice color
        const colors = chart.data.datasets[0].backgroundColor;
        const sliceColor = Array.isArray(colors) ? String(colors[index]) : '#2F82FF';
        const labelRadius = outerRadius * 0.65;

        ctx.fillStyle = getLabelColor(sliceColor);
        ctx.textAlign = 'center';
        ctx.fillText(pctLabel, x + labelRadius * cos, y + labelRadius * sin);
      } else {
        // Thin slice: leader line out from the arc edge to a label beside it.
        // Everything drawn out here must fit inside the chart's layout.padding.
        const lineStart = outerRadius + 1;
        const lineEnd = outerRadius + 7;

        ctx.strokeStyle = 'rgba(45, 45, 45, 0.45)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + lineStart * cos, y + lineStart * sin);
        ctx.lineTo(x + lineEnd * cos, y + lineEnd * sin);
        ctx.stroke();

        ctx.fillStyle = '#2D2D2D';
        ctx.font = '500 10px Inter, sans-serif';
        ctx.textAlign = cos >= 0 ? 'left' : 'right';
        ctx.fillText(pctLabel, x + (lineEnd + 2) * cos, y + (lineEnd + 2) * sin);
      }

      ctx.restore();
    });
  },
};

const PieChart: FC = ({ slices, size = 160, onSliceClick, onSliceHover }: PieChartProps) => {
  const hoveredRef = useRef<number | null>(null);

  return (
    <div style={{ width: size, height: size }} data-testid="pie-chart">
      <Pie
        data={{
          labels: slices.map((slice) => slice.label),
          datasets: [
            {
              data: slices.map((slice) => slice.value),
              backgroundColor: slices.map((slice) => slice.color),
              // A 2px surface-colored ring keeps adjacent slices visually separated
              borderColor: '#FFFFFF',
              borderWidth: 2,
              hoverOffset: 8,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            // Room for the hover offset plus the leader-line labels of thin slices
            padding: 26,
          },
          onClick: (_event, elements: ActiveElement[]) => {
            const index = elements[0]?.index;
            if (index !== undefined && onSliceClick) {
              onSliceClick(slices[index], index);
            }
          },
          onHover: (event, elements: ActiveElement[]) => {
            const index = elements.length > 0 ? elements[0].index : null;
            if (event.native?.target instanceof HTMLElement) {
              event.native.target.style.cursor = index !== null && onSliceClick ? 'pointer' : 'default';
            }
            if (hoveredRef.current !== index) {
              hoveredRef.current = index;
              onSliceHover?.(index);
            }
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
        }}
        plugins={[sliceLabelsPlugin]}
      />
    </div>
  );
};

export default PieChart;
